/**
 * Resultaten-export voor de meetstandaard-onderzoekspagina (superTenant).
 *
 * Berekende resultaten (Cronbach's alpha per effect + factorladingen/
 * eigenwaarden) plus een tabblad 'Data' met de volledige gepseudonimiseerde
 * dataset waarop ze berekend zijn. Besluit (user, 2026-10-19): onderzoekers
 * willen resultaten en rauwe data in één bestand. Het Data-tabblad gebruikt
 * dezelfde opbouw als de export op de dataweergave (buildMsiResearchDataSheet).
 * Spec: docs/features/supertenant-sector-correlations.md
 */
import * as XLSX from 'xlsx'
import { buildMsiResearchDataSheet } from './exportMsiResearchDataXlsx'

/**
 * Puur: bouwt de twee sheet-grids (arrays van rijen) voor de export.
 *
 * @param {object} input
 * @param {Array<{name: string, alpha: number|null, n: number, k: number}>} input.alphaRows - Alpha per effect
 * @param {import('./factorAnalysis').FactorAnalysisResult | null} input.factors - Factoranalyse-resultaat, of null
 * @param {Array<{key: string, effectName: string, itemTitle: string}>} input.columns - Item-kolommen (volgorde = ladingen-rijen)
 * @param {Array<{respondentLabel: string, orgName: string, momentLabel: string, scores: Object<string, number>}>} input.rows - Brede datasetrijen (gepseudonimiseerd)
 * @returns {{alphaSheet: Array<Array<*>>, factorSheet: Array<Array<*>>, dataSheet: Array<Array<*>>}}
 */
export const buildMsiResultsSheets = ({ alphaRows, factors, columns, rows }) => {
    const alphaSheet = [
        ['Effect', 'Items', 'Respondenten (compleet)', 'Cronbachs alpha'],
        ...alphaRows.map((row) => [row.name, row.k, row.n, row.alpha === null ? '' : row.alpha]),
    ]

    const dataSheet = buildMsiResearchDataSheet({ columns, rows })

    if (!factors) {
        return { alphaSheet, factorSheet: [['Te weinig data voor een factoranalyse']], dataSheet }
    }

    const factorSheet = [
        ['Eigenwaarde', 'Verklaarde variantie'],
        ...factors.eigenvalues.map((value, index) => [value, factors.explained[index]]),
        [],
        ['Item', 'Effect', ...Array.from({ length: factors.numFactors }, (_, f) => `F${f + 1}`)],
        ...columns.map((column, itemIndex) => [
            column.itemTitle,
            column.effectName,
            ...factors.loadings[itemIndex],
        ]),
    ]

    return { alphaSheet, factorSheet, dataSheet }
}

/**
 * Bouwt het xlsx-bestand en start de download.
 *
 * @param {object} input - Zelfde shape als buildMsiResultsSheets
 * @param {string} [fileName]
 */
const exportMsiResultsXlsx = (input, fileName = 'meetstandaard-resultaten.xlsx') => {
    const { alphaSheet, factorSheet, dataSheet } = buildMsiResultsSheets(input)

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(alphaSheet), 'Cronbachs alpha')
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(factorSheet), 'Factoranalyse')
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(dataSheet), 'Data')
    XLSX.writeFile(workbook, fileName)
}

export default exportMsiResultsXlsx
